import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2, LibraryBig, CheckCircle2, Trash2, Pencil, Check } from "lucide-react";

export default function ResourceProgram({ studentId, readOnly }) {
  const { user } = useAuth();
  const canToggle = !readOnly || user?.role === "student";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/assigned-units/${studentId}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (u) => {
    try {
      await api.put(`/assigned-units/${studentId}/${u.id}`, { ...u, status: u.status === "Tamamlandı" ? "Bekliyor" : "Tamamlandı" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/assigned-units/${studentId}/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const startEdit = (u) => {
    setEditId(u.id);
    setEditForm({ week: u.week || 1, unit_name: u.unit_name || "", foy_pages: u.foy_pages || "", bank_pages: u.bank_pages || "", questions: u.questions || 0 });
  };
  const saveEdit = async (u) => {
    try {
      await api.put(`/assigned-units/${studentId}/${u.id}`, {
        ...u,
        week: Number(editForm.week || 0),
        unit_name: editForm.unit_name,
        foy_pages: editForm.foy_pages,
        bank_pages: editForm.bank_pages,
        questions: Number(editForm.questions || 0),
      });
      setEditId(null);
      toast.success("Ünite güncellendi");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const sets = {};
  items.forEach((u) => {
    const sn = u.set_name || "Kaynak Programı";
    sets[sn] = sets[sn] || {};
    sets[sn][u.subject] = sets[sn][u.subject] || [];
    sets[sn][u.subject].push(u);
  });
  Object.values(sets).forEach((g) =>
    Object.values(g).forEach((arr) => arr.sort((a, b) => (a.week || 0) - (b.week || 0) || (a.unit_no || 0) - (b.unit_no || 0)))
  );

  return (
    <div className="space-y-6" data-testid="resource-program">
      <div>
        <h3 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><LibraryBig size={20} className="text-indigo-600" /> Otomatik Kaynak Programı</h3>
        <p className="text-sm text-slate-500 mt-0.5">Yayınevi setinden atanan üniteler: föy + soru bankası sayfa aralıklarıyla, hafta hafta.</p>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : items.length === 0 ? (
        <div className="card-ta p-8 text-center text-slate-500">Henüz kaynak programı atanmadı.</div>
      ) : (
        Object.entries(sets).map(([setName, subjects]) => (
          <div key={setName} className="space-y-4">
            <div className="rounded-xl bg-slate-900 text-white px-4 py-2.5 font-display font-bold">{setName}</div>
            {Object.entries(subjects).map(([subject, units]) => {
              const done = units.filter((u) => u.status === "Tamamlandı").length;
              return (
                <div key={subject} className="card-ta overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                    <span className="font-bold text-slate-800 text-sm">{subject}</span>
                    <span className="text-xs text-slate-500">{done}/{units.length} ünite tamamlandı</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                          <th className="px-4 py-2">Hafta</th>
                          <th className="px-4 py-2">Ünite</th>
                          <th className="px-4 py-2">Föy Sayfa</th>
                          <th className="px-4 py-2">Soru Bankası</th>
                          <th className="px-4 py-2 text-right">Soru</th>
                          <th className="px-4 py-2 text-right">Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {units.map((u) => (
                          <tr key={u.id} className={`border-b border-slate-50 ${u.status === "Tamamlandı" ? "bg-emerald-50/60" : ""}`} data-testid={`assigned-unit-${u.id}`}>
                            {editId === u.id ? (
                              <>
                                <td className="px-4 py-2"><input type="number" min="1" className="ta-input !py-1 w-20" value={editForm.week} onChange={(e) => setEditForm({ ...editForm, week: e.target.value })} data-testid={`edit-week-${u.id}`} /></td>
                                <td className="px-4 py-2"><input className="ta-input !py-1" value={editForm.unit_name} onChange={(e) => setEditForm({ ...editForm, unit_name: e.target.value })} data-testid={`edit-unit-${u.id}`} /></td>
                                <td className="px-4 py-2"><input className="ta-input !py-1 w-24" value={editForm.foy_pages} onChange={(e) => setEditForm({ ...editForm, foy_pages: e.target.value })} data-testid={`edit-foy-${u.id}`} /></td>
                                <td className="px-4 py-2"><input className="ta-input !py-1 w-24" value={editForm.bank_pages} onChange={(e) => setEditForm({ ...editForm, bank_pages: e.target.value })} data-testid={`edit-bank-${u.id}`} /></td>
                                <td className="px-4 py-2 text-right"><input type="number" className="ta-input !py-1 w-20 ml-auto" value={editForm.questions} onChange={(e) => setEditForm({ ...editForm, questions: e.target.value })} data-testid={`edit-q-${u.id}`} /></td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                  <button onClick={() => saveEdit(u)} className="text-emerald-600 hover:text-emerald-800 align-middle" title="Kaydet" data-testid={`edit-save-${u.id}`}><Check size={16} /></button>
                                  <button onClick={() => setEditId(null)} className="ml-2 text-slate-400 hover:text-slate-600 align-middle" title="Vazgeç" data-testid={`edit-cancel-${u.id}`}>✕</button>
                                </td>
                              </>
                            ) : (
                              <>
                            <td className="px-4 py-2 text-slate-500 whitespace-nowrap">
                              {u.date ? <span>{u.date}{u.day ? ` · ${u.day}` : ""}</span> : <span>{u.week}. Hafta</span>}
                            </td>
                            <td className="px-4 py-2 font-medium text-slate-800">{u.unit_no}. {u.unit_name}</td>
                            <td className="px-4 py-2 text-slate-600">{u.foy_pages || "-"}</td>
                            <td className="px-4 py-2 text-slate-600">{u.bank_pages || "-"}</td>
                            <td className="px-4 py-2 text-right text-slate-600">{u.questions || "-"}</td>
                            <td className="px-4 py-2 text-right">
                              {!canToggle ? (
                                <span className={`text-xs px-2 py-0.5 rounded-full border ${u.status === "Tamamlandı" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{u.status}</span>
                              ) : (
                                <button onClick={() => toggle(u)} data-testid={`assigned-toggle-${u.id}`}
                                  className={`text-xs px-2.5 py-1 rounded-full border font-semibold inline-flex items-center gap-1 ${u.status === "Tamamlandı" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"}`}>
                                  {u.status === "Tamamlandı" && <CheckCircle2 size={12} />}{u.status}
                                </button>
                              )}
                              {!readOnly && user?.role !== "student" && (
                                <button onClick={() => del(u.id)} className="ml-2 text-slate-300 hover:text-rose-600 align-middle" title="Sil" data-testid={`assigned-del-${u.id}`}><Trash2 size={14} /></button>
                              )}
                              {!readOnly && user?.role !== "student" && (
                                <button onClick={() => startEdit(u)} className="ml-2 text-slate-300 hover:text-indigo-600 align-middle" title="Düzenle" data-testid={`assigned-edit-${u.id}`}><Pencil size={14} /></button>
                              )}
                            </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
